import React, { useState } from "react";
import { ModalContainer } from "../partials/modalbackground";
import { Icon } from "@iconify/react";
import { InputWithIcon } from "../partials/inputs";
import PrimaryBtn from "../partials/btn";
import { Controller, useForm } from "react-hook-form";
import { useApiCall } from "../../data/useFetcher";
import { useUsersStore } from "../../data/stores/loggerStore";
import { IoMdClose } from "react-icons/io";
import toast from "react-hot-toast";

interface FormType {
  departmentName: string;
  description: string;
  departmentHead: string;
  departmentCode: string;
  organizationId: string;
}

const DepartmentModal = ({ handleModal, handleClose }) => {
  const {
      control,
      handleSubmit,
      formState: { errors },
    } = useForm({
      defaultValues: {
        departmentName: "",
        description: "",
        departmentHead: "",
        departmentCode: "",
        organizationId: "",
      },
    }),
    { onSubmitAPI, loading } = useApiCall(),
    { getDynamicLogger, allData }: any = useUsersStore();

  const onSubmit = async (data: FormType) => {
    await onSubmitAPI({
      url: `/api/v1/department/create`,
      type: "post",
      data: data,
      toggleModal: () => {
        handleModal();
        // Refresh the departments list
        // You can add API call here to refresh the departments data
        toast.success("Department created successfully!");
      },
    });
  };

  return (
    <div>
      <ModalContainer>
        <div className="bg-white modal-shadow w-[500px] rounded-xl p-6">
          <div className="flex justify-between items-start mb-4">
            <div
              style={{
                border: "8px solid #E6F3FE",
              }}
              className="size-12 rounded-full flex justify-center items-center bg-[#E6F3FE]"
            >
              <Icon
                icon={"material-symbols:business"}
                color="#2070B8"
                style={{
                  fontSize: "24px",
                }}
              />
            </div>
            <IoMdClose
              size={20}
              onClick={handleClose}
              className="cursor-pointer"
              color="#A6A6A6"
            />
          </div>
          <h4 className="text-lg mb-2 font-medium text-sec">
            Create New Department
          </h4>
          <p className="text-sm font-normal text-[#667085] mb-6">
            Add a new department to your organization structure.
          </p>
          <form onSubmit={handleSubmit(onSubmit)} className="mt-5">
            <div className="space-y-4">
              <div>
                <Controller
                  name="departmentName"
                  control={control}
                  rules={{
                    required: "Department name is required",
                    minLength: {
                      value: 2,
                      message: "Department name must be at least 2 characters",
                    },
                  }}
                  render={({ field: { value, onChange, name } }) => (
                    <InputWithIcon
                      icon="material-symbols:business"
                      placeholder="e.g., Human Resources"
                      label="Department Name"
                      value={value}
                      onChange={onChange}
                      name={name}
                    />
                  )}
                />
                {errors.departmentName && (
                  <p className="text-[#dc2626] text-xs mt-1">
                    {errors.departmentName.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="departmentCode"
                  control={control}
                  rules={{
                    required: "Department code is required",
                    pattern: {
                      value: /^[A-Z0-9]{2,10}$/,
                      message:
                        "Department code must be 2-10 uppercase letters/numbers",
                    },
                  }}
                  render={({ field: { value, onChange, name } }) => (
                    <InputWithIcon
                      icon="material-symbols:tag"
                      placeholder="e.g., HR, IT, FIN"
                      label="Department Code"
                      value={value}
                      onChange={onChange}
                      name={name}
                    />
                  )}
                />
                {errors.departmentCode && (
                  <p className="text-[#dc2626] text-xs mt-1">
                    {errors.departmentCode.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="departmentHead"
                  control={control}
                  rules={{
                    required: "Department head is required",
                  }}
                  render={({ field: { value, onChange, name } }) => (
                    <InputWithIcon
                      icon="material-symbols:person"
                      placeholder="e.g., John Doe"
                      label="Department Head"
                      value={value}
                      onChange={onChange}
                      name={name}
                    />
                  )}
                />
                {errors.departmentHead && (
                  <p className="text-[#dc2626] text-xs mt-1">
                    {errors.departmentHead.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    required: "Description is required",
                    minLength: {
                      value: 10,
                      message: "Description must be at least 10 characters",
                    },
                  }}
                  render={({ field: { value, onChange, name } }) => (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder="Brief description of the department's role and responsibilities..."
                        value={value}
                        onChange={onChange}
                        name={name}
                        rows={3}
                      />
                    </div>
                  )}
                />
                {errors.description && (
                  <p className="text-[#dc2626] text-xs mt-1">
                    {errors.description.message}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-8 gap-4 items-center w-full grid grid-cols-2">
              <button
                onClick={handleClose}
                type="button"
                className="main-shadow main-border h-11 bg-transparent text-es-gray-200 text-base font-medium"
              >
                Cancel
              </button>
              <PrimaryBtn
                text="Create Department"
                width="w-full"
                type="submit"
                loading={!!loading}
              />
            </div>
          </form>
        </div>
      </ModalContainer>
    </div>
  );
};

export default DepartmentModal;
